import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useStore } from '@/state/store';
import { useSimulation } from './useSimulation';
import { CreasePatternEditor } from '@/components/editor2d/CreasePatternEditor';
import { FoldedViewer } from '@/components/viewer3d/FoldedViewer';
import { Toolbar, TOOL_KEYS } from '@/components/ui/Toolbar';
import { Menu, MenuCheck, MenuHead, MenuItem, MenuSep } from '@/components/ui/Menu';
import { AboutDialog, ExamplesDialog, NewSheetDialog } from '@/components/ui/Dialogs';
import { ProjectsDialog } from '@/components/ui/ProjectsDialog';
import { AnalysisPanel } from '@/components/panels/AnalysisPanel';
import { VertexInspector } from '@/components/panels/VertexInspector';
import { CreaseInspector } from '@/components/panels/CreaseInspector';
import { SolverPanel } from '@/components/panels/SolverPanel';
import { ConstructPanel } from '@/components/panels/ConstructPanel';
import { ViewPanel } from '@/components/panels/ViewPanel';
import { LearnPanel } from '@/components/panels/LearnPanel';
import { PerformancePanel } from '@/components/panels/PerformancePanel';
import { EXAMPLES } from '@/examples/library';
import {
  copyShareLink,
  exportObj,
  exportStl,
  exportSvg,
  importSvgFile,
  openFoldFile,
  saveFold,
  saveScreenshot,
} from './fileActions';
import { decodePatternFromUrl } from '@/share/url';
import { autosave, loadAutosave, migrateLegacyStorage } from '@/persistence/idb';
import type { CreaseAssignment } from '@/origami/types';

type DialogId = 'examples' | 'new' | 'about' | 'projects' | null;

export function App(): JSX.Element {
  useSimulation();
  const view = useStore((s) => s.view);
  const setView = useStore((s) => s.setView);
  const [dialog, setDialog] = useState<DialogId>(null);
  const [mobileTab, setMobileTab] = useState<'pattern' | '3d' | 'analysis'>('pattern');
  const isNarrow = useNarrow();

  useTheme(view.theme);
  useStartupPattern();
  useAutosave();
  useShortcuts();

  const showEditor = isNarrow ? mobileTab === 'pattern' : view.layout !== '3d';
  const showViewer = isNarrow ? mobileTab === '3d' : view.layout !== '2d';
  const showSidebar = !isNarrow || mobileTab === 'analysis';

  return (
    <div className="app">
      <MenuBar onDialog={setDialog} />

      {isNarrow ? (
        <div className="mobile-tabs">
          {(['pattern', '3d', 'analysis'] as const).map((t) => (
            <button key={t} aria-pressed={mobileTab === t} onClick={() => setMobileTab(t)}>
              {t === 'pattern' ? 'Pattern' : t === '3d' ? '3D' : 'Analysis'}
            </button>
          ))}
        </div>
      ) : null}

      <div className="workspace">
        <Toolbar />
        <Panes showEditor={showEditor} showViewer={showViewer} />
        {showSidebar ? (
          <aside className="sidebar">
            <VertexInspector />
            <CreaseInspector />
            <AnalysisPanel />
            <ConstructPanel />
            <SolverPanel />
            <ViewPanel />
            <LearnPanel />
            <PerformancePanel />
          </aside>
        ) : null}
      </div>

      <FoldBar />
      <StatusBar />
      <Notices />

      {dialog === 'examples' ? <ExamplesDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'new' ? <NewSheetDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'about' ? <AboutDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'projects' ? <ProjectsDialog onClose={() => setDialog(null)} /> : null}
      {/* Layout buttons live in the View menu; this keeps them reachable by test id. */}
      <div style={{ display: 'none' }}>
        <button data-testid="layout-split" onClick={() => setView({ layout: 'split' })} />
      </div>
    </div>
  );
}

function Panes({
  showEditor,
  showViewer,
}: {
  showEditor: boolean;
  showViewer: boolean;
}): JSX.Element {
  const splitRatio = useStore((s) => s.view.splitRatio);
  const setView = useStore((s) => s.setView);
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent): void => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const ratio = (e.clientX - rect.left) / rect.width;
      setView({ splitRatio: Math.min(0.85, Math.max(0.15, ratio)) });
    };
    const onUp = (): void => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging, setView]);

  const both = showEditor && showViewer;
  return (
    <div className="panes" ref={ref}>
      {showEditor ? (
        <div style={{ flex: both ? `0 0 ${splitRatio * 100}%` : '1', display: 'flex', minWidth: 0 }}>
          <CreasePatternEditor />
        </div>
      ) : null}
      {both ? (
        <div
          className={`divider${dragging ? ' dragging' : ''}`}
          onPointerDown={() => setDragging(true)}
          role="separator"
          aria-orientation="vertical"
        />
      ) : null}
      {showViewer ? (
        <div style={{ flex: 1, display: 'flex', minWidth: 0 }}>
          <FoldedViewer />
        </div>
      ) : null}
    </div>
  );
}

function MenuBar({ onDialog }: { onDialog: (d: Exclude<DialogId, null>) => void }): JSX.Element {
  const store = useStore();
  const { view, setView, undo, redo, past, future, assignSelection, toggleMountainValley, deleteSelection, sim, setSim, resetSimulation } = store;

  return (
    <div className="menubar">
      <div className="brand">
        fold<span>.app</span>
      </div>

      <Menu label="File">
        <MenuItem onClick={() => onDialog('new')} shortcut="⌘N">
          New sheet…
        </MenuItem>
        <MenuItem onClick={() => onDialog('examples')} shortcut="⌘E">
          Examples…
        </MenuItem>
        <MenuItem onClick={() => onDialog('projects')}>Projects…</MenuItem>
        <MenuSep />
        <MenuItem onClick={() => void openFoldFile()} shortcut="⌘O">
          Open FOLD…
        </MenuItem>
        <MenuItem onClick={() => void importSvgFile()}>Import SVG…</MenuItem>
        <MenuSep />
        <MenuItem onClick={saveFold} shortcut="⌘S">
          Save FOLD
        </MenuItem>
        <MenuItem onClick={exportSvg}>Export SVG (crease pattern)</MenuItem>
        <MenuItem onClick={exportObj}>Export OBJ (folded)</MenuItem>
        <MenuItem onClick={exportStl}>Export STL (folded)</MenuItem>
        <MenuItem onClick={saveScreenshot}>Save screenshot</MenuItem>
        <MenuSep />
        <MenuItem onClick={() => void copyShareLink()}>Copy shareable link</MenuItem>
      </Menu>

      <Menu label="Edit">
        <MenuItem onClick={undo} disabled={past.length === 0} shortcut="⌘Z">
          Undo
        </MenuItem>
        <MenuItem onClick={redo} disabled={future.length === 0} shortcut="⇧⌘Z">
          Redo
        </MenuItem>
        <MenuSep />
        <MenuItem onClick={deleteSelection} shortcut="⌫">
          Delete selection
        </MenuItem>
      </Menu>

      <Menu label="Pattern">
        <MenuHead>Assign to selection</MenuHead>
        {(
          [
            ['M', 'Mountain', 'M'],
            ['V', 'Valley', 'V'],
            ['F', 'Flat', 'F'],
            ['B', 'Boundary', 'B'],
            ['C', 'Cut', 'X'],
            ['U', 'Unassigned', 'U'],
          ] as const
        ).map(([code, label, key]) => (
          <MenuItem key={code} onClick={() => assignSelection(code as CreaseAssignment)} shortcut={key}>
            {label}
          </MenuItem>
        ))}
        <MenuSep />
        <MenuItem onClick={toggleMountainValley} shortcut="T">
          Toggle mountain / valley
        </MenuItem>
      </Menu>

      <Menu label="Simulate">
        <MenuItem onClick={() => setSim({ mode: 'compliant' })}>
          Compliant (bar-and-hinge){sim.mode === 'compliant' ? '  ✓' : ''}
        </MenuItem>
        <MenuItem onClick={() => setSim({ mode: 'rigid' })}>
          Rigid origami{sim.mode === 'rigid' ? '  ✓' : ''}
        </MenuItem>
        <MenuSep />
        <MenuItem onClick={() => setSim({ playing: !sim.playing })} shortcut="Space">
          {sim.playing ? 'Pause' : 'Play'}
        </MenuItem>
        <MenuItem onClick={() => setSim({ playDirection: sim.playDirection === 1 ? -1 : 1 })}>
          Reverse
        </MenuItem>
        <MenuItem onClick={resetSimulation} shortcut="R">
          Reset to flat
        </MenuItem>
        <MenuSep />
        <MenuCheck
          checked={sim.collisionEnabled}
          onChange={(v) => setSim({ collisionEnabled: v })}
        >
          Collision detection
        </MenuCheck>
      </Menu>

      <Menu label="View">
        <MenuHead>Layout</MenuHead>
        <MenuItem onClick={() => setView({ layout: '2d' })}>2D only</MenuItem>
        <MenuItem onClick={() => setView({ layout: 'split' })}>Split</MenuItem>
        <MenuItem onClick={() => setView({ layout: '3d' })}>3D only</MenuItem>
        <MenuSep />
        <MenuHead>Camera</MenuHead>
        {(['reset', 'front', 'back', 'top', 'iso'] as const).map((p) => (
          <MenuItem
            key={p}
            onClick={() => window.dispatchEvent(new CustomEvent('origami-camera', { detail: p }))}
          >
            {p[0].toUpperCase() + p.slice(1)}
          </MenuItem>
        ))}
        <MenuSep />
        <MenuCheck checked={view.showGrid} onChange={(v) => setView({ showGrid: v })}>
          Grid
        </MenuCheck>
        <MenuCheck checked={view.showCreaseLines} onChange={(v) => setView({ showCreaseLines: v })}>
          Crease lines
        </MenuCheck>
        <MenuCheck checked={view.wireframe} onChange={(v) => setView({ wireframe: v })}>
          Wireframe
        </MenuCheck>
        <MenuCheck checked={view.showAuxiliary} onChange={(v) => setView({ showAuxiliary: v })}>
          Auxiliary edges
        </MenuCheck>
        <MenuCheck checked={view.showDebugPanel} onChange={(v) => setView({ showDebugPanel: v })}>
          Performance panel
        </MenuCheck>
        <MenuSep />
        <MenuCheck
          checked={view.theme === 'light'}
          onChange={(v) => setView({ theme: v ? 'light' : 'dark' })}
        >
          Light theme
        </MenuCheck>
      </Menu>

      <Menu label="Learn">
        <MenuHead>Examples</MenuHead>
        {EXAMPLES.slice(0, 6).map((e) => (
          <MenuItem key={e.id} onClick={() => store.loadExample(e.id)}>
            {e.name}
          </MenuItem>
        ))}
        <MenuItem onClick={() => onDialog('examples')}>All examples…</MenuItem>
        <MenuSep />
        <MenuItem onClick={() => onDialog('about')}>About &amp; references…</MenuItem>
      </Menu>

      <div className="menu-spacer" />
      <span className="faint mono" style={{ fontSize: 10.5 }}>
        {store.pattern.meta.title ?? 'Untitled'}
      </span>
    </div>
  );
}

function FoldBar(): JSX.Element {
  const sim = useStore((s) => s.sim);
  const setSim = useStore((s) => s.setSim);
  const setFoldAmount = useStore((s) => s.setFoldAmount);
  const resetSimulation = useStore((s) => s.resetSimulation);

  return (
    <div className="foldbar">
      <span className="label">Fold</span>
      <input
        type="range"
        min={-100}
        max={100}
        step={0.5}
        value={sim.foldAmount * 100}
        data-testid="fold-slider"
        aria-label="Fold amount"
        onChange={(e) => setFoldAmount(Number(e.target.value) / 100)}
      />
      <span className="amount" data-testid="fold-amount">
        {(sim.foldAmount * 100).toFixed(0)}%
      </span>
      <div className="seg">
        <button
          onClick={() => setSim({ playing: !sim.playing })}
          aria-pressed={sim.playing}
          title="Play / pause (Space)"
        >
          {sim.playing ? '❚❚' : '▶'}
        </button>
        <button
          onClick={() => setSim({ playDirection: sim.playDirection === 1 ? -1 : 1 })}
          title="Reverse direction"
        >
          ⇄
        </button>
        <button onClick={resetSimulation} title="Reset to flat (R)">
          ⟲
        </button>
      </div>
      <label
        className="label hide-narrow"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        Speed
        <input
          type="range"
          min={0.05}
          max={2}
          step={0.05}
          value={sim.speed}
          style={{ width: 78 }}
          onChange={(e) => setSim({ speed: Number(e.target.value) })}
        />
      </label>
      <div className="seg hide-narrow">
        <button
          aria-pressed={sim.mode === 'compliant'}
          onClick={() => setSim({ mode: 'compliant' })}
        >
          Compliant
        </button>
        <button aria-pressed={sim.mode === 'rigid'} onClick={() => setSim({ mode: 'rigid' })}>
          Rigid
        </button>
      </div>
    </div>
  );
}

function StatusBar(): JSX.Element {
  const analysis = useStore((s) => s.analysis);
  const frame = useStore((s) => s.frame);
  const fps = useStore((s) => s.fps);
  const sim = useStore((s) => s.sim);

  const localOk =
    analysis.kawasakiApplicable > 0 &&
    analysis.kawasakiPassing === analysis.kawasakiApplicable &&
    analysis.maekawaPassing === analysis.maekawaApplicable;

  const residual = frame?.rigid
    ? frame.rigid.loopClosureResidual
    : frame?.compliant
      ? frame.compliant.maxAxialStrain
      : null;

  return (
    <div className="statusbar" data-testid="statusbar">
      <span>
        Local conditions{' '}
        {analysis.kawasakiApplicable === 0 ? (
          <span className="dim">n/a</span>
        ) : localOk ? (
          <span className="ok">✓</span>
        ) : (
          <span className="warn">✕</span>
        )}
      </span>
      <span className="sep" />
      <span>DOF: {analysis.dof.estimatedDof}</span>
      <span className="sep" />
      <span>
        {sim.mode === 'rigid' ? 'Loop residual' : 'Max strain'}:{' '}
        {residual === null ? '—' : residual.toExponential(1)}
      </span>
      <span className="sep" />
      <span>Solve: {frame ? `${frame.solveMilliseconds.toFixed(1)} ms` : '—'}</span>
      <span className="sep" />
      <span>FPS: {fps.toFixed(0)}</span>
      <span className="sep" />
      <span className="dim">
        {analysis.counts.vertices} v · {analysis.counts.edges} e · {analysis.counts.faces} f
        {frame ? ` · ${frame.meshStats.triangles} tri` : ''}
      </span>
    </div>
  );
}

function Notices(): JSX.Element | null {
  const notices = useStore((s) => s.notices);
  const dismiss = useStore((s) => s.dismissNotice);
  if (notices.length === 0) return null;
  return (
    <div className="notices">
      {notices.map((n) => (
        <div className={`notice ${n.severity}`} key={n.id}>
          <span style={{ flex: 1 }}>{n.message}</span>
          <button onClick={() => dismiss(n.id)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

// --- hooks ---------------------------------------------------------------

/**
 * Below this width the split view stops being useful and the app switches to
 * tabs. Kept in sync with the media query in styles.css.
 */
const NARROW_BREAKPOINT = 720;

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= NARROW_BREAKPOINT : false,
  );
  useEffect(() => {
    const onResize = (): void => setNarrow(window.innerWidth <= NARROW_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function useTheme(theme: 'dark' | 'light'): void {
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
}

/**
 * Restore a shared URL, else the autosave, else keep the default example.
 *
 * Both lookups are asynchronous, so the user can already have loaded an example
 * or drawn something by the time they resolve. Restoring on top of that would
 * silently throw their work away, so the pattern they started with is captured
 * up front and the restore is abandoned if it has changed.
 */
function useStartupPattern(): void {
  useEffect(() => {
    let cancelled = false;
    const initial = useStore.getState().pattern;
    const stillUntouched = (): boolean => useStore.getState().pattern === initial;

    void (async () => {
      // Adopt anything saved under the app's previous name before reading.
      await migrateLegacyStorage();
      const shared = await decodePatternFromUrl(location.search);
      if (cancelled || !stillUntouched()) return;
      if (shared) {
        useStore.getState().loadPattern(shared, 'Open shared link');
        useStore.getState().notify('info', 'Loaded a crease pattern from the link.');
        return;
      }
      const saved = await loadAutosave();
      if (cancelled || !saved || !stillUntouched()) return;
      useStore.getState().loadPattern(saved.pattern, 'Restore session');
      useStore.getState().notify('info', 'Restored your previous session.');
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}

function useAutosave(): void {
  useEffect(() => {
    let timer = 0;
    const unsubscribe = useStore.subscribe((state, previous) => {
      if (state.pattern === previous.pattern) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void autosave(state.pattern), 1200);
    });
    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);
}

function useShortcuts(): void {
  const handler = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
    const store = useStore.getState();
    const mod = e.metaKey || e.ctrlKey;

    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) store.redo();
      else store.undo();
      return;
    }
    if (mod && e.key.toLowerCase() === 's') {
      e.preventDefault();
      saveFold();
      return;
    }
    if (mod && e.key.toLowerCase() === 'o') {
      e.preventDefault();
      void openFoldFile();
      return;
    }
    if (mod) return;

    if (TOOL_KEYS[e.key]) {
      store.setTool(TOOL_KEYS[e.key]);
      return;
    }
    switch (e.key) {
      case 'Backspace':
      case 'Delete':
        e.preventDefault();
        store.deleteSelection();
        break;
      case ' ':
        e.preventDefault();
        store.setSim({ playing: !store.sim.playing });
        break;
      case 'm':
      case 'M':
        store.assignSelection('M');
        break;
      case 'v':
      case 'V':
        store.assignSelection('V');
        break;
      case 'b':
      case 'B':
        store.assignSelection('B');
        break;
      case 'u':
      case 'U':
        store.assignSelection('U');
        break;
      case 'x':
      case 'X':
        store.assignSelection('C');
        break;
      case 't':
      case 'T':
        store.toggleMountainValley();
        break;
      case 'r':
      case 'R':
        store.resetSimulation();
        break;
      case 'Escape':
        store.setSelection([]);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handler]);
}
